// Fieldstone lander behavior. Three jobs:
//
// 1. [data-fieldstone-form] (hero email capture, both landers on mobile and
//    /fieldstone-free on desktop): subscribe the email to the Fieldstone list
//    — its Klaviyo flow sends the font itself.
// 2. [data-fieldstone-app-form] (mobile app CTA, both landers): subscribe to
//    the app-download list. On a Mac that happens directly; anywhere else
//    the Mac check dialog asks first, because a phone's user agent can't tell
//    us whether the visitor owns a Mac desktop. "Notify me about Windows"
//    goes to the separate Windows waitlist, and is disabled if
//    KLAVIYO_WINDOWS_LIST_ID is set to an empty string.
// 3. [data-fieldstone-download] (/fieldstone-mac desktop CTA): fire the Fathom
//    event the ad set is measured by. The click itself keeps the existing
//    email-capture-modal behavior (direct download on Macs, modal elsewhere).
//
// Every subscribe carries a lander_source property so the two ad sets stay
// separable in Klaviyo now that both landers share the mobile page. The Klaviyo
// submit path itself is shared with the app email modal — one implementation,
// imported from email-capture-modal.ts.

import { subscribeToKlaviyo, trackEvent, isMacDesktop } from './email-capture-modal';

// Which lander the visitor is on, off [data-lander-source] on the .fieldstone
// root. Only these two templates load this module, so a missing attribute is a
// markup bug worth surfacing rather than papering over with a default.
function landerSource(): string {
  const root = document.querySelector<HTMLElement>('[data-lander-source]');
  const source = root?.dataset.landerSource;
  if (!source) {
    throw new Error('[fieldstone-lander] the .fieldstone root is missing data-lander-source.');
  }
  return source;
}

// Fathom events carry no properties, so the lander is baked into the name and
// the two ad sets stay separable: landerEvent('capture') reads
// "fieldstone-free-capture" on one lander and "fieldstone-mac-capture" on the
// other. The Mac check dialog's event is 'platform-check-open' rather than
// 'mac-check-open' so the Mac lander's copy doesn't read "…-mac-mac-check…".
function landerEvent(name: string): string {
  return `${landerSource()}-${name}`;
}

class LanderForm {
  readonly form: HTMLFormElement;
  readonly companyId: string;
  readonly listId: string;
  private readonly input: HTMLInputElement;
  private readonly submitButton: HTMLButtonElement;
  private readonly errorInvalid: HTMLElement | null;
  private readonly errorNetwork: HTMLElement | null;
  private readonly successState: HTMLElement | null;
  private submitting = false;

  constructor(form: HTMLFormElement, private readonly onSubmit: (form: LanderForm) => Promise<void>) {
    const input = form.querySelector<HTMLInputElement>('input[type="email"]');
    const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!input || !submitButton) {
      throw new Error('[fieldstone-lander] form is missing its email input or submit button.');
    }
    this.form = form;
    this.companyId = form.dataset.klaviyoCompanyId ?? '';
    this.listId = form.dataset.klaviyoListId ?? '';
    this.input = input;
    this.submitButton = submitButton;
    this.errorInvalid = form.querySelector<HTMLElement>('[data-fieldstone-error-invalid]');
    this.errorNetwork = form.querySelector<HTMLElement>('[data-fieldstone-error-network]');
    // The success block is the form's sibling so the whole form can disappear.
    this.successState = form.parentElement?.querySelector<HTMLElement>('[data-fieldstone-success]') ?? null;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.handleSubmit();
    });
  }

  email(): string {
    return this.input.value.trim();
  }

  private async handleSubmit(): Promise<void> {
    if (this.submitting) return;
    this.hideErrors();

    if (!this.input.checkValidity()) {
      if (this.errorInvalid) this.errorInvalid.hidden = false;
      this.input.focus();
      return;
    }

    this.submitting = true;
    this.setLoading(true);
    try {
      await this.onSubmit(this);
    } finally {
      this.submitting = false;
      this.setLoading(false);
    }
  }

  setLoading(loading: boolean): void {
    this.submitButton.disabled = loading;
    this.submitButton.classList.toggle('is-loading', loading);
  }

  hideErrors(): void {
    if (this.errorInvalid) this.errorInvalid.hidden = true;
    if (this.errorNetwork) this.errorNetwork.hidden = true;
  }

  showNetworkError(): void {
    if (this.errorNetwork) this.errorNetwork.hidden = false;
  }

  showSuccess(title?: string, sub?: string): void {
    if (!this.successState) return;
    if (title) {
      const titleEl = this.successState.querySelector<HTMLElement>('.fieldstone__success-title');
      if (titleEl) titleEl.textContent = title;
    }
    if (sub) {
      const subEl = this.successState.querySelector<HTMLElement>('.fieldstone__success-sub');
      if (subEl) subEl.textContent = sub;
    }
    this.form.hidden = true;
    this.successState.hidden = false;
    this.successState.querySelector<HTMLElement>('[tabindex="-1"]')?.focus();
  }
}

// The Mac check dialog. One instance serves every app form on the page; the
// form whose submit opened it is remembered so its success state gets shown.
class MacCheckDialog {
  private pendingForm: LanderForm | null = null;
  private readonly macButton: HTMLButtonElement | null;
  private readonly windowsButton: HTMLButtonElement | null;
  private readonly error: HTMLElement | null;
  private savedScrollY = 0;

  constructor(private readonly dialog: HTMLDialogElement) {
    this.macButton = dialog.querySelector<HTMLButtonElement>('[data-mac-check-mac]');
    this.windowsButton = dialog.querySelector<HTMLButtonElement>('[data-mac-check-windows]');
    this.error = dialog.querySelector<HTMLElement>('[data-mac-check-error]');

    dialog.querySelector('[data-mac-check-close]')?.addEventListener('click', () => dialog.close());
    // Backdrop clicks land on the dialog element itself.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    // Same body scroll lock (and CSS class) as the app email modal; one
    // `close` listener restores scroll for button, backdrop, and ESC alike.
    dialog.addEventListener('close', () => {
      document.body.classList.remove('app-email-modal-open');
      document.body.style.top = '';
      window.scrollTo(0, this.savedScrollY);
    });

    this.macButton?.addEventListener('click', () => {
      void this.choose(this.macButton!, (form) =>
        subscribeToApp(form).then(() => {
          form.showSuccess();
          trackEvent(landerEvent('app-capture'));
        })
      );
    });

    this.windowsButton?.addEventListener('click', () => {
      const windowsListId = this.dialog.dataset.windowsListId ?? '';
      // No list id means KLAVIYO_WINDOWS_LIST_ID was set to an empty string
      // (the button is also disabled in the template; this is the belt to
      // that suspender).
      if (!windowsListId) return;
      void this.choose(this.windowsButton!, (form) =>
        subscribeToKlaviyo({
          companyId: form.companyId,
          listId: windowsListId,
          email: form.email(),
          customSource: 'Fieldstone lander Windows waitlist',
          properties: { lander_source: landerSource() },
        }).then(() => {
          form.showSuccess(
            this.dialog.dataset.windowsSuccessTitle,
            this.dialog.dataset.windowsSuccessSub
          );
          trackEvent(landerEvent('windows-waitlist'));
        })
      );
    });
  }

  open(form: LanderForm): void {
    if (this.dialog.open) return;
    this.pendingForm = form;
    if (this.error) this.error.hidden = true;
    this.savedScrollY = window.scrollY;
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.classList.add('app-email-modal-open');
    this.dialog.showModal();
    trackEvent(landerEvent('platform-check-open'));
  }

  private async choose(button: HTMLButtonElement, action: (form: LanderForm) => Promise<void>): Promise<void> {
    const form = this.pendingForm;
    if (!form) return;
    if (this.error) this.error.hidden = true;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      await action(form);
      this.dialog.close();
    } catch {
      if (this.error) this.error.hidden = false;
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

function subscribeToApp(form: LanderForm): Promise<void> {
  return subscribeToKlaviyo({
    companyId: form.companyId,
    listId: form.listId,
    email: form.email(),
    customSource: 'Fieldstone lander app CTA',
    properties: { download_request_platform: 'mac', lander_source: landerSource() },
  });
}

function initFieldstoneLander(): void {
  // Hero form: the font itself, by email.
  document.querySelectorAll<HTMLFormElement>('[data-fieldstone-form]').forEach((el) => {
    new LanderForm(el, async (form) => {
      try {
        await subscribeToKlaviyo({
          companyId: form.companyId,
          listId: form.listId,
          email: form.email(),
          customSource: 'Fieldstone lander font CTA',
          properties: { lander_source: landerSource() },
        });
        form.showSuccess();
        trackEvent(landerEvent('capture'));
      } catch {
        form.showNetworkError();
      }
    });
  });

  // App CTA form: direct on Macs, Mac check dialog everywhere else.
  const dialogEl = document.querySelector<HTMLDialogElement>('[data-mac-check-dialog]');
  const dialog = dialogEl ? new MacCheckDialog(dialogEl) : null;

  document.querySelectorAll<HTMLFormElement>('[data-fieldstone-app-form]').forEach((el) => {
    new LanderForm(el, async (form) => {
      if (isMacDesktop() || !dialog) {
        try {
          await subscribeToApp(form);
          form.showSuccess();
          trackEvent(landerEvent('app-capture'));
        } catch {
          form.showNetworkError();
        }
        return;
      }
      dialog.open(form);
    });
  });

  // Mac lander download CTA: the click-through the ad set is measured by.
  document.querySelectorAll('[data-fieldstone-download]').forEach((el) => {
    el.addEventListener('click', () => trackEvent(landerEvent('download-click')));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFieldstoneLander);
} else {
  initFieldstoneLander();
}
